import React from 'react';
import { Row, Col, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useNFe } from '../../../../hooks/useNFe';

interface SecaoTotaisProps {
  nfeId: string;
}

const SecaoTotais: React.FC<SecaoTotaisProps> = ({ nfeId }) => {
  const { data: nfe } = useNFe(nfeId);
  const totais = nfe?.totais;
  // Format currency
  const formatarMoeda = (valor?: number) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor);
  };

  return (
    <>
      <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-3">
        <div className="d-flex align-items-start">
          <FontAwesomeIcon icon={faInfoCircle} className="text-info-emphasis me-2 mt-1" />
          <div className="text-info-emphasis small">
            Os totais são calculados automaticamente pelo sistema com base nos itens adicionados.
            Esta seção é somente leitura.
          </div>
        </div>
      </Alert>

      <Row className="g-3">
        {/* Totais de Valores */}
        <Col xs={12}>
          <h6 className="text-body-emphasis mb-3">Totais de Valores</h6>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total dos Produtos</div>
              <div className="h5 mb-0 text-body-emphasis">{formatarMoeda(totais.valorProdutos)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total de Desconto</div>
              <div className="h5 mb-0 text-danger-emphasis">{formatarMoeda(totais.valorDesconto)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total de Frete</div>
              <div className="h5 mb-0 text-body-emphasis">{formatarMoeda(totais.valorFrete)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total de Seguro</div>
              <div className="h5 mb-0 text-body-emphasis">{formatarMoeda(totais.valorSeguro)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Outras Despesas</div>
              <div className="h5 mb-0 text-body-emphasis">{formatarMoeda(totais.valorOutros)}</div>
            </Card.Body>
          </Card>
        </Col>

        {/* Totais de Tributos */}
        <Col xs={12}>
          <h6 className="text-body-emphasis mb-3 mt-3">Totais de Tributos</h6>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total ICMS</div>
              <div className="h6 mb-0 text-body-emphasis">{formatarMoeda(totais.valorICMS)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total IPI</div>
              <div className="h6 mb-0 text-body-emphasis">{formatarMoeda(totais.valorIPI)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total PIS</div>
              <div className="h6 mb-0 text-body-emphasis">{formatarMoeda(totais.valorPIS)}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Card className="border border-opacity-25 bg-body-secondary">
            <Card.Body className="p-3">
              <div className="text-body-secondary small mb-1">Total COFINS</div>
              <div className="h6 mb-0 text-body-emphasis">{formatarMoeda(totais.valorCOFINS)}</div>
            </Card.Body>
          </Card>
        </Col>

        {/* Total da NFe - Destaque */}
        <Col xs={12}>
          <Card className="border border-primary border-opacity-50 bg-primary-subtle mt-3">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-primary-emphasis fw-semibold mb-1">Total da NFe</div>
                  <div className="text-body-secondary small">
                    Valor final da nota fiscal eletrônica
                  </div>
                </div>
                <div className="display-6 fw-bold text-primary-emphasis">
                  {formatarMoeda(totais.valorNota)}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SecaoTotais;
